import axios, {
  AxiosError,
  AxiosResponse,
  AxiosInstance,
  AxiosRequestConfig,
  InternalAxiosRequestConfig,
} from 'axios';
import { getToken, getTokenWithRetry } from '../tokenUtils';
import { endAgentSession, isAgentLaunch } from '../agentSession';
import { redirectToPlumApp } from '../redirectToPlumApp';
import { logger } from '../logger';
import AppConfig from '@/AppConfig';

const JOURNEY_REQUEST_PATH = '/relay/rhi-v2/journey';
const TOKEN_NOT_FOUND_ERROR = 'Token not found in cookies';
const TOKEN_MISSING_FLAG = '_plumTokenMissing';
const AUTH_RETRY_CONFIG_KEY = '_plumAuthRetry';
const LOGOUT_DEBOUNCE_RESET_MS = 5000;
let logoutInProgress = false;
let logoutResetTimeout: ReturnType<typeof setTimeout> | null = null;

export const axiosBaseConfig: AxiosRequestConfig = {
  timeout: 60000,
};

export const isNetworkError = (error: unknown): boolean => {
  const axiosError = error as AxiosError | undefined;

  return (
    !axiosError?.response &&
    (axiosError?.code === 'ERR_NETWORK' ||
      axiosError?.code === 'ECONNABORTED' ||
      axiosError?.message === 'Network Error')
  );
};

export const isTransientError = (error: unknown): boolean => {
  if (!error) return false;
  return axios.isCancel(error) || isNetworkError(error) || !!(error as any)?.[TOKEN_MISSING_FLAG];
};

export const retryOnTransientError = (failureCount: number, error: Error) =>
  failureCount < 1 && isTransientError(error);

type AuthRetryConfig = AxiosRequestConfig & {
  [AUTH_RETRY_CONFIG_KEY]?: boolean;
};

const getXPlumTokenHeader = (config: AxiosRequestConfig) => {
  const headers = config.headers as
    | (Record<string, string> & { get?: (name: string) => string | null })
    | undefined;

  if (typeof headers?.get === 'function') {
    return headers.get('x-plum-token');
  }

  return headers?.['x-plum-token'];
};

const setXPlumTokenHeader = (config: AxiosRequestConfig, token: string) => {
  const headers = config.headers as
    | (Record<string, string> & { set?: (name: string, value: string) => void })
    | undefined;

  if (typeof headers?.set === 'function') {
    headers.set('x-plum-token', token);
    return;
  }

  const headerObject = (headers ?? {}) as Record<string, string>;
  config.headers = {
    ...headerObject,
    'x-plum-token': token,
  } as AxiosRequestConfig['headers'];
};

const toLoggableError = (error: AxiosError) => ({
  message: error?.message,
  code: error?.code,
  status: error?.response?.status,
  method: error?.config?.method,
  url: error?.config?.url,
});

const isJourneyRequest = (config?: AxiosRequestConfig) =>
  (config?.url ?? '').includes(JOURNEY_REQUEST_PATH);

const resetLogoutGateAfterDelay = () => {
  if (logoutResetTimeout) {
    clearTimeout(logoutResetTimeout);
  }

  logoutResetTimeout = setTimeout(() => {
    logoutInProgress = false;
    logoutResetTimeout = null;
  }, LOGOUT_DEBOUNCE_RESET_MS);
};

const triggerLogoutOnce = (config?: AxiosRequestConfig) => {
  if (logoutInProgress) {
    return;
  }

  if (isAgentLaunch()) {
    if (!isJourneyRequest(config)) {
      return;
    }

    logoutInProgress = true;
    endAgentSession();
    resetLogoutGateAfterDelay();
    return;
  }

  logoutInProgress = true;

  if (typeof window?.NativeBridge?.onNativeLogout === 'function') {
    window.NativeBridge.onNativeLogout();
  } else {
    const redirectToUrl = encodeURI(window.location.href);
    redirectToPlumApp(`/logout?redirectTo=${redirectToUrl}`)();
  }

  resetLogoutGateAfterDelay();
};

const recoverTokenBeforeLogout = async () => {
  if (logoutInProgress) {
    return;
  }

  if (isAgentLaunch()) {
    return;
  }

  logoutInProgress = true;

  const recoveredToken = await getTokenWithRetry();

  if (recoveredToken) {
    logoutInProgress = false;
    return;
  }

  if (typeof window?.NativeBridge?.onNativeLogout === 'function') {
    window.NativeBridge.onNativeLogout();
  } else {
    const redirectToUrl = encodeURI(window.location.href);
    redirectToPlumApp(`/logout?redirectTo=${redirectToUrl}`)();
  }

  resetLogoutGateAfterDelay();
};

const retryRequestWithFreshToken = async (
  axiosInstance: AxiosInstance,
  error: AxiosError,
) => {
  const originalConfig = error.config as AuthRetryConfig | undefined;
  if (!originalConfig || originalConfig[AUTH_RETRY_CONFIG_KEY]) {
    return null;
  }

  const freshToken = await getTokenWithRetry();
  if (!freshToken) {
    return null;
  }

  originalConfig[AUTH_RETRY_CONFIG_KEY] = true;
  setXPlumTokenHeader(originalConfig, freshToken);
  return axiosInstance(originalConfig);
};

export const setupInterceptors = (
  axiosInstance: AxiosInstance,
  isDev?: boolean,
) => {
  axiosInstance.interceptors.request.use(
    async (config) => {
      const existingXPlumToken = getXPlumTokenHeader(config);
      if (existingXPlumToken === 'skip') {
        delete config.headers['x-plum-token'];
        return config;
      }

      const authRetryConfig = config as AuthRetryConfig;
      let xPlumToken = authRetryConfig[AUTH_RETRY_CONFIG_KEY]
        ? existingXPlumToken
        : getToken();
      if (!xPlumToken) {
        xPlumToken = await getTokenWithRetry();
        if (!xPlumToken) {
          const tokenMissingError = new AxiosError(
            TOKEN_NOT_FOUND_ERROR,
            'ERR_TOKEN_MISSING',
            config as InternalAxiosRequestConfig,
          );
          (tokenMissingError as any)[TOKEN_MISSING_FLAG] = true;
          return Promise.reject(tokenMissingError);
        }
      }

      setXPlumTokenHeader(config, xPlumToken);
      if (isDev) {
        const internalToken = AppConfig.INTERNAL_TOKEN;
        if (internalToken) {
          config.headers['x-plum-internal-token'] = internalToken;
        } else {
          window.dev.e('Internal token not found in AppConfig');
        }
      }
      return config;
    },
    (error) => {
      window.dev.e('Error in request interceptor', toLoggableError(error));
      logger.error('Error in request interceptor', toLoggableError(error));
      return Promise.reject(error);
    },
  );

  axiosInstance.interceptors.response.use(
    (response: AxiosResponse) => response,
    async (error: AxiosError) => {
      window.dev.e('Error in response interceptor', toLoggableError(error));
      logger.error('Error in response interceptor', toLoggableError(error));
      window.dev.e(error.message === TOKEN_NOT_FOUND_ERROR);

      if ((error as any)[TOKEN_MISSING_FLAG]) {
        const originalConfig = error.config as AuthRetryConfig | undefined;
        if (originalConfig && !originalConfig[AUTH_RETRY_CONFIG_KEY]) {
          const recoveredToken = await getTokenWithRetry();
          if (recoveredToken) {
            originalConfig[AUTH_RETRY_CONFIG_KEY] = true;
            setXPlumTokenHeader(originalConfig, recoveredToken);
            return axiosInstance(originalConfig);
          }
        }
        await recoverTokenBeforeLogout();
      } else if (error.response?.status === 401) {
        const retriedResponse = await retryRequestWithFreshToken(
          axiosInstance,
          error,
        );
        if (retriedResponse) {
          return retriedResponse;
        }

        triggerLogoutOnce(error.config);
      }

      return Promise.reject(error);
    },
  );
};

export default { axiosBaseConfig, setupInterceptors, isTransientError };
